import { useCallback, useState } from 'react';
import { LOCALE_NAMES, LOCALE_TAGS, LOCALES, useI18n } from '../../i18n/index.tsx';
import { TEXT_SCALES, THEME_CHOICES, type ThemeChoice } from '../../persistence/preferences.ts';
import { IconButton } from '../../ui/IconButton.tsx';
import { Popover, PopoverChoice } from '../../ui/Popover.tsx';
import { CheckIcon, FlagIcon, FocusIcon, ThemeIcon, TypographyIcon } from '../../ui/icons.tsx';

/** Which popover is open, if any. Only ever one: they occupy the same corner. */
type OpenPanel = 'text' | 'language' | 'theme' | null;

/**
 * The four controls in the corner of the header.
 *
 * Icons only, all the same size, all the same shape. Together they are about how the page is read
 * rather than about the exam — which is why they sit together and why none of them carries a label
 * competing with the question underneath.
 *
 * Focus is the odd one out and looks it: it is a toggle rather than a menu, so it is the only one
 * that can appear switched on, and the only one with no popover.
 */
export function HeaderControls({
  textScale,
  onTextScale,
  onLocale,
  theme,
  onTheme,
  focusAvailable,
  focusOn,
  onToggleFocus,
}: {
  readonly textScale: number;
  readonly onTextScale: (value: number) => void;
  readonly onLocale: (value: string) => void;
  readonly theme: ThemeChoice;
  readonly onTheme: (value: ThemeChoice) => void;
  /** False when there is no question on screen; the control is then not offered at all. */
  readonly focusAvailable: boolean;
  readonly focusOn: boolean;
  readonly onToggleFocus: () => void;
}) {
  const { t, locale, setLocale } = useI18n();
  const [open, setOpen] = useState<OpenPanel>(null);

  const close = useCallback(() => {
    setOpen(null);
  }, []);

  const toggle = (panel: Exclude<OpenPanel, null>): void => {
    setOpen((current) => (current === panel ? null : panel));
  };

  return (
    <div className="header-controls">
      <span className="header-controls__anchor">
        <IconButton
          name={t('controls.textSize')}
          description={t('controls.textSize.description')}
          icon={<TypographyIcon />}
          expanded={open === 'text'}
          controls="panel-text-size"
          onClick={() => {
            toggle('text');
          }}
        />

        {open === 'text' && (
          <Popover id="panel-text-size" label={t('controls.textSize')} onClose={close}>
            {TEXT_SCALES.map((scale) => (
              <PopoverChoice
                key={scale}
                selected={scale === textScale}
                onSelect={() => {
                  onTextScale(scale);
                  close();
                }}
              >
                {/* Each row is set at the size it selects, so the choice shows itself. */}
                <span
                  className="popover__sample"
                  style={{ fontSize: `${String(scale / 100)}rem` }}
                >
                  {t('app.textSizeValue', { percent: scale })}
                </span>
                {scale === textScale && <CheckIcon />}
              </PopoverChoice>
            ))}
          </Popover>
        )}
      </span>

      <span className="header-controls__anchor">
        <IconButton
          name={t('controls.language')}
          description={t('controls.language.description')}
          icon={<FlagIcon locale={locale} />}
          expanded={open === 'language'}
          controls="panel-language"
          onClick={() => {
            toggle('language');
          }}
        />

        {open === 'language' && (
          <Popover id="panel-language" label={t('controls.language')} onClose={close}>
            {LOCALES.map((code) => (
              <PopoverChoice
                key={code}
                selected={code === locale}
                onSelect={() => {
                  setLocale(code);
                  onLocale(code);
                  close();
                }}
              >
                <FlagIcon locale={code} />
                <span className="popover__label">
                  {LOCALE_NAMES[code]}
                  <span className="popover__tag">{LOCALE_TAGS[code]}</span>
                </span>
                {code === locale && <CheckIcon />}
              </PopoverChoice>
            ))}
          </Popover>
        )}
      </span>

      {focusAvailable && (
        <span className="header-controls__anchor">
          <IconButton
            name={t('controls.focus')}
            description={t('controls.focus.description')}
            icon={<FocusIcon />}
            pressed={focusOn}
            onClick={onToggleFocus}
          />
        </span>
      )}

      <span className="header-controls__anchor">
        <IconButton
          name={t('controls.theme')}
          description={t('controls.theme.description')}
          icon={<ThemeIcon />}
          expanded={open === 'theme'}
          controls="panel-theme"
          onClick={() => {
            toggle('theme');
          }}
        />

        {open === 'theme' && (
          <Popover id="panel-theme" label={t('controls.theme')} onClose={close}>
            {THEME_CHOICES.map((choice) => (
              <PopoverChoice
                key={choice}
                selected={choice === theme}
                onSelect={() => {
                  onTheme(choice);
                  close();
                }}
              >
                <span className="popover__label">{t(`controls.theme.${choice}` as const)}</span>
                {choice === theme && <CheckIcon />}
              </PopoverChoice>
            ))}
          </Popover>
        )}
      </span>
    </div>
  );
}
